import React, {useEffect} from 'react'
import {useSelector} from 'react-redux'
import Select from 'react-select'
import actions from 'redux-auto'
import i18n from 'i18next'
import Toggle from '#components/a/Toggle'
import {usePermissions} from '#hooks/usePermissions'
import {useAbteilungen} from '#hooks/store/useAbteilungen'
import globalFilter from '#tools/globalFilter'

const generateArrayOfObjectsForSelect = arrayOfValues => {
  if (arrayOfValues === undefined) {
    return []
  }
  return arrayOfValues.map(value => {
    return {value: value, label: value}
  })
}

function onlyUnique(value, index, self) {
  return self.indexOf(value) === index
}

function Filter() {
  const authentication = useSelector(state => state.authentication)
  const myAbteilungen = authentication.abteilungen
  const permissions = usePermissions()
  const canFilterByMyAbteilungen = permissions.meineAzubis.canFilterAzubisByMyAbteilungen
  const hasAbteilungsFilter = permissions.filter.hasAbteilungsFilter
  const {abteilungen} = useAbteilungen()
  const loadGetAzubisWithAbteilungen = useSelector(state => state.filter?.loadGetAzubisWithAbteilungen)

  const filter = useSelector(state => state.filter)
  const isLoadingAbteilungen = useSelector(state => state.versetzungsplan.isLoading)
  const {
    azubis,
    azubisWithAbteilungen,
    selectedBerufe,
    selectedMaerkte,
    selectedAusbildungsjahre,
    selectedAbteilungen,
    selectedIds,
    filterMyAbteilung,
  } = filter

  useEffect(() => {
    actions.filter.setFilterMyAbteilung({
      filterMyAbteilung: filterMyAbteilung === false ? false : canFilterByMyAbteilungen,
      myAbteilungen: myAbteilungen,
    })
  }, [canFilterByMyAbteilungen, myAbteilungen, filterMyAbteilung])

  useEffect(() => {
    const matchingAzubiIds = globalFilter(
      azubis,
      azubisWithAbteilungen,
      selectedBerufe,
      selectedMaerkte,
      selectedAusbildungsjahre,
      selectedAbteilungen,
      selectedIds,
      filterMyAbteilung,
      myAbteilungen,
    )
    actions.filter.setMatchingAzubiIds({matchingAzubiIds})
  }, [
    azubis,
    azubisWithAbteilungen,
    selectedBerufe,
    selectedMaerkte,
    selectedAusbildungsjahre,
    selectedAbteilungen,
    selectedIds,
    filterMyAbteilung,
    myAbteilungen,
  ])

  useEffect(() => {
    if (!isLoadingAbteilungen && hasAbteilungsFilter) {
      actions.filter.getAzubisWithAbteilungen({azubiIds: azubis.map(u => u.id)})
    }
  }, [isLoadingAbteilungen])

  const berufe = azubis.map(azubi => azubi.ausbildungsBerufString).filter(onlyUnique)
  const maerkte = azubis.map(azubi => azubi.markt).filter(onlyUnique)
  const ausbildungsjahre = azubis
    .map(azubi => azubi.ausbildungsjahr)
    .filter(onlyUnique)
    .sort()
  const ids = azubis.map(azubi => azubi.id).filter(onlyUnique)

  const berufeSelectable = generateArrayOfObjectsForSelect(berufe)
  const maerkteSelectable = generateArrayOfObjectsForSelect(maerkte)
  const yearsSelectable = generateArrayOfObjectsForSelect(ausbildungsjahre)
  const idsSelectable = generateArrayOfObjectsForSelect(ids)
  const abteilungenSelectable = abteilungen
    ? abteilungen.map(abteilung => {
        return {value: abteilung.id, label: abteilung.name}
      })
    : []

  const onSelectedChange = (changedObjects, action) => {
    let changedValues = changedObjects.map(changedObject => changedObject.value)
    action({selectedItems: changedValues})
  }
  const onSelectedChangeAbteilung = (changedObjects, action) => {
    action({selectedItems: changedObjects})
  }
  const indicatorStyle = provided => ({
    ...provided,
    color: 'rgb(234, 234, 234)',
    '&:hover': {
      color: 'rgb(153, 153, 153)',
    },
  })
  const filterStyles = {
    option: provided => ({
      ...provided,
      backgroundColor: 'transparent',
      '&:hover': {
        color: 'rgb(252, 229, 49)',
      },
    }),

    control: provided => ({
      ...provided,
      backgroundColor: '#343638',
      font: 'inherit',
      position: 'relative',
      borderRadius: '0px',
      border: '0px',
      boxShadow: '0px',
      maxHeight: '105px',
      overflowX: 'hidden',
    }),
    clearIndicator: indicatorStyle,
    dropdownIndicator: indicatorStyle,
    menu: provided => ({
      ...provided,
      backgroundColor: '#343638',
      zIndex: 9999,
    }),

    menuList: provided => ({
      ...provided,
      color: 'white',
      '::-webkit-scrollbar': {
        width: '7px',
        height: '0px',
      },
      '::-webkit-scrollbar-track': {
        background: '#f1f1f1',
      },
      '::-webkit-scrollbar-thumb': {
        background: '#888',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: '#555',
      },
    }),

    input: provided => ({
      ...provided,
      color: 'white',
    }),

    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1
      const transition = 'opacity 300ms'

      return {...provided, opacity, transition}
    },
  }

  return (
    <div className="filterComponent">
      <Select
        styles={filterStyles}
        className="filterIdSelect"
        options={idsSelectable}
        value={generateArrayOfObjectsForSelect(selectedIds)}
        onChange={changedObjects => onSelectedChange(changedObjects, actions.filter.setSelectedIds)}
        placeholder={i18n.t('filter_userIds')}
        isMulti
      />
      <Select
        className="filterBerufe"
        styles={filterStyles}
        options={berufeSelectable}
        value={generateArrayOfObjectsForSelect(selectedBerufe)}
        onChange={changedObjects => onSelectedChange(changedObjects, actions.filter.setSelectedBerufe)}
        placeholder={i18n.t('filter_ausbildungsberufe')}
        isMulti
      />
      <Select
        styles={filterStyles}
        className="filterMaerkte"
        options={maerkteSelectable}
        onChange={changedObjects => onSelectedChange(changedObjects, actions.filter.setSelectedMaerkte)}
        value={generateArrayOfObjectsForSelect(selectedMaerkte)}
        placeholder={i18n.t('filter_maerkte')}
        isMulti
      />
      <Select
        styles={filterStyles}
        className="filterAusbildungsjahre"
        options={yearsSelectable}
        onChange={changedObjects => onSelectedChange(changedObjects, actions.filter.setSelectedAusbildungsjahre)}
        value={generateArrayOfObjectsForSelect(selectedAusbildungsjahre)}
        placeholder={i18n.t('filter_ausbildungsjahre')}
        isMulti
      />
      {hasAbteilungsFilter && (
        <Select
          styles={filterStyles}
          options={abteilungenSelectable}
          onChange={changedObjects => onSelectedChangeAbteilung(changedObjects, actions.filter.setSelectedAbteilungen)}
          className="filterAbteilungen"
          value={selectedAbteilungen}
          placeholder={i18n.t('filter_abteilungen')}
          isMulti
          isDisabled={loadGetAzubisWithAbteilungen}
          isLoading={loadGetAzubisWithAbteilungen}
        />
      )}

      {canFilterByMyAbteilungen && (
        <Toggle
          active={!filterMyAbteilung}
          callback={() =>
            actions.filter.setFilterMyAbteilung({
              filterMyAbteilung: !filterMyAbteilung,
              myAbteilungen: myAbteilungen,
            })
          }
          text={i18n.t('ausbildungsnachweiseListe_filtertoggle')}
        />
      )}
    </div>
  )
}

Filter.propTypes = {}

export default Filter
